import { EyeOutlined } from '@ant-design/icons';
import { getPreClearanceTableData } from '@app/api/pre-clearance.api';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { notificationController } from '@app/controllers/notificationController';
import { useMounted } from '@app/hooks/useMounted';
import { Pagination } from '@app/models/Pagination';
import { PreClearanceTableRow, StatusOption } from '@app/models/pre_clearance/PreClearanceResponse';
import { TablePaginationConfig, Space, Tooltip, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

const PreClearanceTable = () => {
  const [tableData, setTableData] = useState<{
    data: PreClearanceTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);

  const fetch = useCallback(
    (pagination: Pagination) => {
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== null && v !== undefined && v !== ''),
      );

      setTableData((prev) => ({ ...prev, loading: true }));
      getPreClearanceTableData({ ...pagination, ...activeFilters })
        .then((res) => {
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: res.pagination,
              loading: false,
            });
            setStatusOptions(res.status_options);
          }
        })
        .catch((error: any) => {
          if (isMounted.current) {
            setTableData({ data: [], pagination: initialPagination, loading: false });
          }
          notificationController.error({ message: error.message });
        });
    },
    [isMounted, filters],
  );

  // Runs on mount and whenever filters change (fetch is recreated when filters change)
  useEffect(() => {
    fetch({ current: 1, pageSize: tableData.pagination.pageSize });
  }, [fetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || initialPagination.pageSize,
    });
  };

  const handleViewRow = (id: number) => {
    navigate(`/view-pre-clearance`, { state: { id } });
  };

  const getStatusLabel = (value: string) => statusOptions.find((o) => o.value === value)?.label ?? value;

  const showTotal = (total: number, range: [number, number]): React.ReactNode => (
    <span style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}>
      {`Showing ${range[0]} - ${range[1]} of ${total} Pre-Clearance`}
    </span>
  );

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'status',
      placeholder: 'Status',
      type: 'dropdown',
      options: { statusOptions },
    },
  ];

  const columns: ColumnsType<PreClearanceTableRow> = [
    {
      title: 'Code',
      dataIndex: 'reference_code',
      key: 'reference_code',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => getStatusLabel(value),
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: 'H. S. Code',
      dataIndex: 'hs_code',
      key: 'hs_code',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              type="text"
              onClick={() => handleViewRow(record.id)}
              icon={<EyeOutlined />}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <BaseCard id="basic-table" title="Pre-Clearance" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        rowKey="id"
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};

export default PreClearanceTable;
